import React from 'react';
import groupBy from 'lodash/groupBy';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';

import {
  Icon,
  Modal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
  ButtonsWrapper,
} from '@thinknet-gmbh/platform-common-ui';
import { GanttPlacementConflictTypesEnum } from '@thinknet-gmbh/platform-common';

import {
  WarningWrapper,
  IconWrapper,
  SubmitButton,
  CancelButton,
} from './time-stripe-adding-modal.styles';
import { TimeStripeAddingModalProps } from './time-stripe-adding-modal.interface';
import { PlacementConflictRow } from './placement-conflict-row';
import { CONFLICTS_TO_ICONS_RELATIONS } from './time-stripe-adding-modal.constant';

export const TimeStripeAddingModal: React.FC<TimeStripeAddingModalProps> = ({
  onSubmit,
  onCancel,
  conflicts = [],
}) => {
  const { t } = useTranslation(['common', 'gantt']);
  const groupedConflict = groupBy(conflicts, 'type');
  const validConflicts = Object.values(groupedConflict).map(doc => doc[0]);
  const hasError = validConflicts.some(doc => doc.type === GanttPlacementConflictTypesEnum.ERROR);

  return (
    <Modal isOpened title={t('gantt:labels.timeStripeAddingConfirmation')} onClose={onCancel}>
      <WarningWrapper>
        <IconWrapper>
          <Icon
            rightMargin={false}
            icon={ICONS.infoCircle}
            color={PLATFORM_THEME_COLORS.tone.yellow}
            size={24}
          />
        </IconWrapper>
        <div>
          <Typography
            fontColor={PLATFORM_THEME_COLORS.tone.yellow}
            size={TYPOGRAPHY_SIZE.medium}
            weight={'bold'}
          >
            {t('gantt:messages.timeStripeAddingConfirmationTitle')}
          </Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey} size={TYPOGRAPHY_SIZE.small}>
            {t('gantt:messages.timeStripeAddingConfirmationSubtitle')}
          </Typography>
        </div>
      </WarningWrapper>

      {hasError ? (
        <PlacementConflictRow
          key={GanttPlacementConflictTypesEnum.ERROR}
          icon={ICONS.errorCircleSolid}
          title={t('gantt:labels.conflicts.error')}
        />
      ) : (
        validConflicts.map(({ type }) => (
          <PlacementConflictRow
            key={type}
            icon={CONFLICTS_TO_ICONS_RELATIONS[type]}
            title={t(`gantt:labels.conflicts.${camelCase(type)}`)}
          />
        ))
      )}

      <ButtonsWrapper marginTop={14}>
        {!hasError && <SubmitButton onClick={onSubmit}>{t('common:buttons.confirm')}</SubmitButton>}
        <CancelButton onClick={onCancel}>{t('common:buttons.cancel')}</CancelButton>
      </ButtonsWrapper>
    </Modal>
  );
};
